class Grid {
  constructor(options = {}) {
    // Default settings
    this.settings = {
      rootSelector: '.js-grid',
      renderSelector: '.js-grid-render',
      itemSelector: '.js-grid-item',
      columns: 4,
      gap: '1.5rem',
      balanceColumns: false, // New option for column height balancing
      balanceThreshold: 0.25, // Height difference threshold (as percentage) that triggers rebalancing
      breakpoints: [
        { width: 768, columns: 2, gap: '1rem' },
        { width: 480, columns: 1, gap: '0.75rem' }
      ],
      resizeDelay: 250, // Delay in ms before considering resize "stopped"
      // Callbacks
      onInit: null,      // Called when grid is initialized
      onReady: null,     // Called when grid is fully rendered
      onResize: null,    // Called during resize
      onResizeStop: null, // Called when resize has stopped
      onDistribute: null, // Called when items are distributed
      onBalance: null,    // New callback for when columns are balanced
      ...options
    };

    // Initialize resize timeout reference
    this.resizeTimeout = null;

    // Track current rendered items
    this.currentItems = [];

    this.init();
  }

  init() {
    // Call onInit callback if provided
    if (typeof this.settings.onInit === 'function') {
      this.settings.onInit(this);
    }

    // Get the grid container
    this.container = document.querySelector(this.settings.rootSelector);
    if (!this.container) return;

    // Grab all items from the container
    this.items = Array.from(this.container.querySelectorAll(this.settings.itemSelector));
    if (this.items.length === 0) return;

    // Make the original container sr-only
    this.makeSrOnly(this.container);

    this.createRenderContainer();

    // Create columns with the initial configuration
    const initialConfig = this.getLayoutConfig();
    this.createColumns(initialConfig.columns);

    this.distributeItems();

    // Apply column balancing if the option is enabled, with a slight delay to ensure images load
    if (this.settings.balanceColumns) {
      // Initial balance for basic structure
      this.balanceColumns();

      // Second balance after images have had more time to load
      setTimeout(() => {
        this.balanceColumns();
      }, 500);

      // Final balance after all content should be loaded
      setTimeout(() => {
        this.balanceColumns();
      }, 1500);
    }

    this.setupResponsive();

    // Call onReady callback if provided
    if (typeof this.settings.onReady === 'function') {
      this.settings.onReady(this);
    }
  }

  // Add sr-only class to make content accessible only to screen readers
  makeSrOnly(element) {
    element.classList.add('sr-only');
    // Add common sr-only styles inline as a fallback
    element.style.position = 'absolute';
    element.style.width = '1px';
    element.style.height = '1px';
    element.style.padding = '0';
    element.style.margin = '-1px';
    element.style.overflow = 'hidden';
    element.style.clip = 'rect(0, 0, 0, 0)';
    element.style.whiteSpace = 'nowrap';
    element.style.borderWidth = '0';
  }

  createRenderContainer() {
    // Create the render container with grid layout
    this.renderContainer = document.createElement('div');
    this.renderContainer.className = this.settings.renderSelector.replace('.', '');
    this.renderContainer.setAttribute('aria-hidden', 'true');
    this.applyGridStyles(this.settings.columns, this.settings.gap);

    // Insert the render container after the original grid
    this.container.parentNode.insertBefore(this.renderContainer, this.container.nextSibling);
  }

  applyGridStyles(columns, gap) {
    this.renderContainer.style.display = 'grid';
    this.renderContainer.style.gridTemplateColumns = `repeat(${columns}, 1fr)`;
    this.renderContainer.style.gap = gap; // Gap for the container columns
  }

  createColumns(numColumns) {
    // Clear existing columns
    this.columns = [];

    // Create columns (ul elements) based on the provided number of columns
    for (let i = 0; i < numColumns; i++) {
      const ul = document.createElement('ul');
      ul.style.listStyle = 'none';
      ul.style.padding = '0';
      ul.style.margin = '0';

      // Apply flex layout for better height behavior
      ul.style.display = 'flex';
      ul.style.flexDirection = 'column';
      ul.style.gap = this.settings.gap; // Same gap as the parent

      this.columns.push(ul);
      this.renderContainer.appendChild(ul);
    }
  }

  distributeItems() {
    // Clear current items tracking array
    this.currentItems = [];

    // Get the current layout configuration
    const currentConfig = this.getLayoutConfig();

    // Distribute items across the columns using the current configuration's column count
    this.items.forEach((item, index) => {
      const columnIndex = index % currentConfig.columns; // Use currentConfig.columns
      const clonedItem = item.cloneNode(true);
      clonedItem.style.height = 'fit-content';
      this.columns[columnIndex].appendChild(clonedItem);

      // Track the current rendered items
      this.currentItems.push({
        originalIndex: index,
        columnIndex: columnIndex,
        element: clonedItem
      });
    });

    // Call onDistribute callback if provided
    if (typeof this.settings.onDistribute === 'function') {
      this.settings.onDistribute(this, this.columns);
    }
  }

  // Complete rework of column balancing method
  balanceColumns() {
    // Make sure columns are rendered and measured
    if (!this.columns || this.columns.length <= 1) return;

    // Allow some time for the browser to calculate heights
    setTimeout(() => {
      // First, collect all items and their heights
      const allItems = [...this.currentItems];
      this._collectItemHeights(allItems);

      // Clear all columns
      this.columns.forEach(column => {
        while (column.firstChild) {
          column.removeChild(column.firstChild);
        }
      });

      // Reset current items tracking
      this.currentItems = [];

      // Sort items by height (tallest first)
      allItems.sort((a, b) => (b.height || 0) - (a.height || 0));

      // Use a greedy approach - always place the next tallest item
      // into the shortest column at that point
      const columnHeights = Array(this.columns.length).fill(0);

      allItems.forEach(item => {
        // Find the shortest column
        let shortestColumnIndex = 0;
        for (let i = 1; i < columnHeights.length; i++) {
          if (columnHeights[i] < columnHeights[shortestColumnIndex]) {
            shortestColumnIndex = i;
          }
        }

        // Place item in shortest column
        this.columns[shortestColumnIndex].appendChild(item.element);

        // Update item tracking
        item.columnIndex = shortestColumnIndex;
        this.currentItems.push(item);

        // Update column height
        columnHeights[shortestColumnIndex] += (item.height || 0) + this._getGapInPixels();
      });

      // Call onBalance callback if provided
      if (typeof this.settings.onBalance === 'function') {
        this.settings.onBalance(this, this.columns.map(column => column.offsetHeight));
      }
    }, 50); // Slightly longer delay to ensure accurate measurements
  }

  // Helper method to collect item heights
  _collectItemHeights(items) {
    items.forEach(item => {
      // Store the element's computed height
      const height = item.element.offsetHeight;
      item.height = height;
    });
  }

  // Helper method to get gap size in pixels
  _getGapInPixels() {
    // Check if we have a numeric value or a string like "1.5rem"
    const gap = this.settings.gap;
    if (typeof gap === 'number') {
      return gap;
    }

    // For string values, create a temporary element to measure
    const temp = document.createElement('div');
    temp.style.visibility = 'hidden';
    temp.style.position = 'absolute';
    temp.style.marginTop = gap;
    document.body.appendChild(temp);

    const gapPixels = temp.offsetTop;
    document.body.removeChild(temp);

    return gapPixels;
  }

  setupResponsive() {
    if (!this.settings.breakpoints || this.settings.breakpoints.length === 0) return;

    // Create and add a style element for media queries
    const style = document.createElement('style');
    document.head.appendChild(style);

    let cssRules = '';

    // Create media queries for each breakpoint
    this.settings.breakpoints.forEach(breakpoint => {
      cssRules += `
        @media (max-width: ${breakpoint.width}px) {
          ${this.settings.renderSelector} {
            grid-template-columns: repeat(${breakpoint.columns}, 1fr) !important;
            gap: ${breakpoint.gap} !important;
          }

          ${this.settings.renderSelector} > ul {
            gap: ${breakpoint.gap} !important;
          }
        }
      `;
    });

    style.textContent = cssRules;

    // Bind handlers once to avoid memory leaks
    this.boundResizeHandler = this.handleResize.bind(this);
    this.boundDebouncedResizeHandler = this.handleResizeStop.bind(this);

    // Add resize handler to adjust layout during resize
    window.addEventListener('resize', this.boundResizeHandler);

    // Add debounced resize handler for resize stop events
    window.addEventListener('resize', () => {
      // Clear the previous timeout
      clearTimeout(this.resizeTimeout);

      // Set a new timeout
      this.resizeTimeout = setTimeout(this.boundDebouncedResizeHandler, this.settings.resizeDelay);
    });
  }

  handleResize() {
    // Get current layout configuration based on window width
    const currentConfig = this.getLayoutConfig();

    if (currentConfig) {
      // Clear existing columns
      while (this.renderContainer.firstChild) {
        this.renderContainer.removeChild(this.renderContainer.firstChild);
      }

      // Update the container's grid
      this.applyGridStyles(currentConfig.columns, currentConfig.gap);

      // Create new columns based on current config
      this.createColumns(currentConfig.columns);

      // Redistribute items
      this.distributeItems();

      // Apply column balancing if enabled
      if (this.settings.balanceColumns) {
        // Initial balance
        this.balanceColumns();

        // Additional balance after images have loaded
        setTimeout(() => {
          this.balanceColumns();
        }, 500);
      }

      // Call onResize callback if provided
      if (typeof this.settings.onResize === 'function') {
        this.settings.onResize(this, currentConfig);
      }

      // Call onDistribute callback if provided
      if (typeof this.settings.onDistribute === 'function') {
        this.settings.onDistribute(this, this.columns);
      }
    }
  }

  // Handler for resize stop event
  handleResizeStop() {
    // Get current layout configuration
    const currentConfig = this.getLayoutConfig();

    // Apply column balancing on resize stop if enabled
    if (this.settings.balanceColumns) {
      this.balanceColumns();
    }

    // Call onResizeStop callback if provided
    if (typeof this.settings.onResizeStop === 'function') {
      this.settings.onResizeStop(this, currentConfig);
    }
  }

  // Fixed method to get the appropriate layout configuration based on window width
  getLayoutConfig() {
    const windowWidth = window.innerWidth;

    // Sort breakpoints in ascending order
    const breakpoints = [...this.settings.breakpoints].sort((a, b) => a.width - b.width);

    // Find the first breakpoint that applies (smallest breakpoint that's larger than window width)
    for (let i = 0; i < breakpoints.length; i++) {
      if (windowWidth <= breakpoints[i].width) {
        return breakpoints[i];
      }
    }

    // If no breakpoint applies, return the default settings
    return {
      columns: this.settings.columns,
      gap: this.settings.gap
    };
  }

  // Enhanced version of the getColumnHeights method
  getColumnHeights() {
    if (!this.columns) return [];
    return this.columns.map(column => ({
      element: column,
      height: column.offsetHeight,
      itemCount: column.children.length
    }));
  }

  // Public method to manually trigger a resize
  refresh() {
    this.handleResize();

    // Trigger resize stop event as well
    clearTimeout(this.resizeTimeout);
    this.handleResizeStop();
  }

  // Public method to manually trigger column balancing
  balance() {
    if (this.settings.balanceColumns) {
      this.balanceColumns();
    }
  }

  // Public method to update settings and rebuild the grid
  update(newSettings = {}) {
    // Update settings
    this.settings = {
      ...this.settings,
      ...newSettings
    };

    // Clear existing grid
    if (this.renderContainer) {
      this.renderContainer.remove();
    }

    // Reset current items tracking
    this.currentItems = [];

    // Rebuild grid
    this.init();
  }

  // Public method to destroy the grid and clean up
  destroy() {
    // Remove event listeners
    window.removeEventListener('resize', this.boundResizeHandler);

    // Clear any pending timeouts
    clearTimeout(this.resizeTimeout);

    // Remove the render container
    if (this.renderContainer) {
      this.renderContainer.remove();
    }

    // Restore original container visibility
    if (this.container) {
      this.container.classList.remove('sr-only');
      this.container.style = '';
    }

    // Clear current items tracking
    this.currentItems = [];
  }
}

export default Grid;
